/**
 * 验证码输入纯组件
 * 
 * 这是一个纯UI组件，专注于验证码输入界面的展示和基础交互。
 * 不包含任何业务逻辑和API请求，所有业务逻辑由父组件处理。
 * 
 * 主要功能：
 * - 显示验证码输入界面（图标、标题、描述文本）
 * - 处理6位数验证码输入
 * - 验证码发送倒计时UI显示
 * - 重新发送验证码按钮UI交互
 * - 完全支持国际化
 * 
 * @author 开发团队
 * @version 2.0.0
 */

import { useTranslation } from 'react-i18next'
import { PasswordInput } from 'react-vant'
import CustomButton from '@/components/CustomButton'
import { useVerificationCode } from './useVerificationCode'
import './index.css'

/**
 * 验证码输入组件的属性接口
 */
export interface VerificationCodeInputProps {
  /** 发送验证码的目标账户（手机号、邮箱等） */
  account: string
  /** 验证码提交回调函数 */
  onSubmit: (code: string) => void
  /** 重新发送验证码回调函数 */
  onResendCode: () => Promise<void> | void
  /** 初始倒计时时间（秒），默认60秒 */
  initialCountdown?: number
  /** 重新发送验证码的加载状态，由父组件控制 */
  resendLoading?: boolean
  /** 是否在组件初始化时自动开始倒计时，默认true */
  autoStartCountdown?: boolean
  /** 提交验证码的加载状态，由父组件控制 */
  submitLoading?: boolean
  /** 自定义标题文本，不传则使用默认国际化文本 */
  title?: string
  /** 自定义描述文本，不传则使用默认国际化文本 */
  description?: string
  /** 自定义图标路径，默认使用注册场景图标 */
  icon?: string
  /** 自定义提交按钮文本，不传则使用默认"完成"文本 */
  submitButtonText?: string
  /** 额外的CSS类名 */
  className?: string
  /** 是否显示顶部图标，默认为true */
  showIcon?: boolean
  /** 是否显示标题，默认为true */
  showTitle?: boolean
  /** 是否显示描述文本，默认为true */
  showDescription?: boolean
  /** 标题对齐方式，默认为left */
  align?: 'left' | 'center'
}

/**
 * 验证码输入组件的主要实现
 * 
 * @param props - 组件属性
 * @returns 验证码输入组件JSX元素
 */
export default function VerificationCodeInput({
  account,
  onSubmit,
  onResendCode,
  initialCountdown = 60,
  resendLoading = false,
  submitLoading = false,
  autoStartCountdown = true,
  submitButtonText,
  className = "",
  showTitle = true,
  align = 'left',
}: VerificationCodeInputProps) {
  // 国际化翻译钩子
  const { t } = useTranslation()

  // 使用简化的验证码钩子，只处理UI状态管理
  const {
    code,              // 当前输入的验证码
    countdown,         // 重新发送倒计时（秒）
    canResend,         // 是否可以重新发送验证码
    isCodeValid,       // 验证码是否有效（长度为6位）
    handleCodeChange,  // 处理验证码输入变化
    handleSubmit,      // 处理验证码提交
    startCountdown,    // 开始倒计时
  } = useVerificationCode({
    initialCountdown,
    onSubmit,
    autoStartCountdown
  })

  /**
   * 处理重新发送验证码
   * 调用父组件传入的回调函数
   */
  const handleResendCode = async () => {
    if (!canResend || resendLoading) return

    try {
      await onResendCode()
      // 重新发送成功后，重新开始倒计时
      startCountdown(initialCountdown)
    } catch (error) {
      // 错误处理由父组件负责
      console.error('重新发送验证码失败:', error)
    }
  }

  return (
    <div className={`${className}`}>
      {showTitle && <div className={`text-7 font-medium text-black ${align === 'center' ? 'text-center' : 'text-left'}`}>
        {t('输入安全验证码')}
      </div>}
      <div className={`text-base text-[#86909C] mt-2 break-all ${align === 'center' ? 'text-center' : 'text-left'}`}>
        {t('验证码已发送至')} <span className="text-black break-all">{account}</span>
      </div>

      {/* 验证码输入框区域 - 用户输入6位验证码 */}
      <div className="mt-6 mb-8">
        <PasswordInput
          className="verification-code-input"
          value={code}
          gutter={10}
          length={6}
          mask={false}
          validator={(val: string) => {
            return /^[0-9]{0,6}$/.test(val);
          }}
          onChange={handleCodeChange}
        />
      </div>

      {/* 提交按钮区域 - 验证码输入完成后提交 */}
      <div className="mx-auto">
        <CustomButton
          onClick={handleSubmit}
          loading={submitLoading}
          disabled={!isCodeValid || submitLoading}  // 只有验证码有效时才能提交
        >
          {submitButtonText || t('确认')}
        </CustomButton>
      </div>

      {/* 重新发送验证码按钮区域 - 包含倒计时功能 */}
      <div className="mt-8 text-center">
        <span className="text-3.25 text-[#1A1A1A]">{t('收不到验证码')}</span>
        <button
          onClick={handleResendCode}
          disabled={!canResend || resendLoading}
          className={`text-sm font-medium ml-1 transition-colors ${canResend && !resendLoading
            ? 'text-[var(--primary-color)]'  // 可点击状态：蓝色
            : 'text-[var(--primary-color)]'  // 倒计时中也显示蓝色
            }`}
        >
          {/* 根据不同状态显示不同文本：发送中 / 重新发送 / 倒计时 */}
          {resendLoading
            ? t('发送中')
            : canResend
              ? t('重新发送')
              : t('重新发送X', { countdown })
          }
        </button>
      </div>
    </div>
  )
}